import { X } from "lucide-react";
import { useState } from "react";

export default function EditProfileModal({ userDetails, onClose, onSave, authToken }) {
  const [editedDetails, setEditedDetails] = useState({
    fullName: userDetails?.name || "",
    phoneNumber: userDetails?.phone || "",
    address: {
      street: userDetails?.address?.street || "",
      city: userDetails?.address?.city || "",
      state: userDetails?.address?.state || "",
      zipCode: userDetails?.address?.zip || "",
      country: userDetails?.address?.country || "India"
    }
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8000";

  // List of Indian states and union territories
  const indianStates = [
    "Andaman and Nicobar Islands",
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chandigarh",
    "Chhattisgarh",
    "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jammu and Kashmir",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Ladakh",
    "Lakshadweep",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Puducherry",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear validation errors when user starts typing
    if (validationErrors[name] || (name.includes('.') && validationErrors[name.split('.')[1]])) {
      setValidationErrors(prev => {
        const newErrors = {...prev};
        if (name.includes('.')) {
          delete newErrors[name.split('.')[1]];
        } else {
          delete newErrors[name];
        }
        return newErrors;
      });
    }
    
    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setEditedDetails({
        ...editedDetails,
        address: { ...editedDetails.address, [addressField]: value },
      });
    } else {
      setEditedDetails({ ...editedDetails, [name]: value });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Phone validation
    if (editedDetails.phoneNumber) {
      if (!editedDetails.phoneNumber.isDigit || editedDetails.phoneNumber.length !== 10) {
        if (!/^\d{10}$/.test(editedDetails.phoneNumber)) {
          errors.phoneNumber = 'Must be 10 digits';
        }
      }
    }
    
    // Address validation - check if any address field is filled
    const addressFields = Object.values(editedDetails.address || {});
    const hasAnyAddressData = addressFields.some(field => field && field.trim() !== '');
    
    if (hasAnyAddressData) {
      const requiredFields = ['street', 'city', 'state', 'zipCode', 'country'];
      const missingFields = requiredFields.filter(field => 
        !editedDetails.address[field] || editedDetails.address[field].trim() === ''
      );
      
      if (missingFields.length > 0) {
        errors.address = `Missing fields: ${missingFields.join(', ')}`;
      }
      
      // Zip code validation
      if (editedDetails.address.zipCode && !/^\d{5,6}$/.test(editedDetails.address.zipCode)) {
        errors.zipCode = 'Must be 5 or 6 digits';
      }
    }
    
    return errors;
  };

  const handleSaveChanges = async () => {
    setIsSubmitting(true);
    setError("");
    setValidationErrors({});
    
    try {
      // Client-side validation
      const clientErrors = validateForm();
      if (Object.keys(clientErrors).length > 0) {
        setValidationErrors(clientErrors);
        setIsSubmitting(false);
        return;
      }
      
      // Format data to match backend expectations
      const updatePayload = {
        phone: editedDetails.phoneNumber || "",
        address: {
          street: editedDetails.address.street || "",
          city: editedDetails.address.city || "",
          state: editedDetails.address.state || "",
          zip: editedDetails.address.zipCode || "",
          country: editedDetails.address.country || "India"
        }
      };
      
      // Remove empty address if all fields are empty
      const addressValues = Object.values(updatePayload.address);
      const hasAddressData = addressValues.some(value => value.trim() !== '');
      if (!hasAddressData) {
        updatePayload.address = null;
      }
      
      // Make API call to update user details
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add authorization header if authToken is provided
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/update-user-details/${userDetails.email}/`, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify(updatePayload),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        console.error('API Error Response:', {
          status: response.status,
          statusText: response.statusText,
          data: data
        });
        
        // Handle different error types
        if (response.status === 403) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 401) {
          throw new Error('You are not authorized to perform this action.');
        } else if (data.errors) {
          setValidationErrors(data.errors);
          return;
        }
        throw new Error(data.error || `Failed to update profile (${response.status})`);
      }
      
      // Pass success back to parent component with updated user data
      onSave({
        ...userDetails,
        phone: editedDetails.phoneNumber,
        address: updatePayload.address,
        name: editedDetails.fullName,
        is_profile_complete: data.user?.is_profile_complete || false
      });
      
    } catch (err) {
      setError(err.message || 'An error occurred while updating your profile');
      console.error("Error updating profile:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFieldError = (fieldName) => {
    return validationErrors[fieldName];
  };

  return (
    <>
      {/* Inline CSS for modal animation and layout */}
      <style>
        {`
          @keyframes modalFadeIn {
            0% { opacity: 0; transform: scale(0.95); }
            100% { opacity: 1; transform: scale(1); }
          }
          .modal-animate {
            animation: modalFadeIn 0.3s ease-out forwards;
          }
          .modal-container {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 50;
          }
          .form-grid {
            display: grid;
            grid-template-columns: 1fr; /* Single column on mobile */
            gap: 1rem;
          }
          @media (min-width: 768px) {
            .form-grid {
              grid-template-columns: 1fr 1fr; /* Two columns on desktop */
            }
          }
          .error-field {
            border-color: #ef4444 !important;
          }
          .error-text {
            color: #ef4444;
            font-size: 0.75rem;
            margin-top: 0.25rem;
          }
        `}
      </style>

      {/* Modal Container */}
      <div className="modal-container">
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={onClose}
        ></div>

        {/* Modal Content */}
        <div className="relative bg-white rounded-sm shadow-lg p-6 max-w-lg w-full max-h-[70vh] md:max-h-[50vh] overflow-y-auto modal-animate">
          <div className="flex justify-between items-center mb-4">
            <h2
              className="text-xl md:text-2xl text-[#333333]"
              style={{ fontFamily: "serif" }}
            >
              Edit Profile
            </h2>
            <button
              onClick={onClose}
              className="text-[#333333] hover:text-[#D4AF37]"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-sm text-sm">
              {error}
            </div>
          )}

          {validationErrors.address && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-sm text-sm">
              Address error: {validationErrors.address}
            </div>
          )}

          {/* Form in Two-Column Grid */}
          <div className="form-grid">
            <div>
              <label className="text-sm text-gray-500">Full Name</label>
              <input
                type="text"
                name="fullName"
                value={editedDetails.fullName || ""}
                onChange={handleInputChange}
                className="w-full py-2 px-3 rounded-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm text-[#333333]"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-500">Phone Number</label>
              <input
                type="text"
                name="phoneNumber"
                value={editedDetails.phoneNumber || ""}
                onChange={handleInputChange}
                pattern="[0-9]{10}"
                maxLength="10"
                placeholder="10-digit number"
                className={`w-full py-2 px-3 rounded-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm text-[#333333] ${
                  getFieldError('phoneNumber') || getFieldError('phone') ? 'error-field' : ''
                }`}
              />
              {(getFieldError('phoneNumber') || getFieldError('phone')) && (
                <div className="error-text">{getFieldError('phoneNumber') || getFieldError('phone')}</div>
              )}
            </div>
            
            <div>
              <label className="text-sm text-gray-500">Street</label>
              <input
                type="text"
                name="address.street"
                value={editedDetails.address?.street || ""}
                onChange={handleInputChange}
                className="w-full py-2 px-3 rounded-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm text-[#333333]"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-500">City</label>
              <input
                type="text"
                name="address.city"
                value={editedDetails.address?.city || ""}
                onChange={handleInputChange}
                className="w-full py-2 px-3 rounded-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm text-[#333333]"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-500">State</label>
              <select
                name="address.state"
                value={editedDetails.address?.state || ""}
                onChange={handleInputChange}
                className="w-full py-2 px-3 rounded-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm text-[#333333]"
              >
                <option value="" disabled>
                  Select State
                </option>
                {indianStates.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-sm text-gray-500">Zip Code</label>
              <input
                type="text"
                name="address.zipCode"
                value={editedDetails.address?.zipCode || ""}
                onChange={handleInputChange}
                placeholder="5 or 6 digits"
                className={`w-full py-2 px-3 rounded-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm text-[#333333] ${
                  getFieldError('zipCode') || getFieldError('zip') ? 'error-field' : ''
                }`}
              />
              {(getFieldError('zipCode') || getFieldError('zip')) && (
                <div className="error-text">{getFieldError('zipCode') || getFieldError('zip')}</div>
              )}
            </div>
            
            <div>
              <label className="text-sm text-gray-500">Country</label>
              <select
                name="address.country"
                value={editedDetails.address?.country || "India"}
                onChange={handleInputChange}
                className="w-full py-2 px-3 rounded-sm border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm text-[#333333]"
              >
                <option value="India">India</option>
              </select>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-[#333333] border border-gray-300 rounded-sm hover:bg-gray-100 transition-all duration-200"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveChanges}
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#D4AF37] text-black rounded-sm hover:bg-[#C19B2E] transition-all duration-200 flex items-center"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}